import React, { useState } from "react";
import { DatePicker, Switch } from "antd";
import { AiOutlineCalendar } from "react-icons/ai";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import DropDownNew from "../Form/DropDownNew";
import CustomButton from "../Form/CustomButton";
import Input from "../Form/Input";

import "antd/dist/reset.css"; // for Ant Design v5+

const { RangePicker } = DatePicker;

// Custom Date Range Picker
const CustomDateRangePicker = ({ startDate, endDate, onRangeChange }) => {
  return (
    <div className="flex items-center gap-2">
      <RangePicker
        onChange={onRangeChange}
        value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : []}
        format="YYYY-MM-DD"
        allowClear
        className="border border-gray-300 rounded-md px-[15px] w-[240px] h-10"
      />
    </div>
  );
};

const TableHeader = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = "Search...",
  filters = [],
  dateRangeFilter = null,
  buttons = [],
  datePicker = null,
  containerClass = "",
  searchInputClass = "",
  filterDropdownClass = "",
  datePickerClass = "",
  buttonWrapperClass = "",
  searchWrapperClass = "",
  buttonClass = "",
  dropdownWrapperClass = "",
  isAutoRemainder = false,
  isUserLoading = false,
  isAutoRemainderDisabled = false,
  isAutoRemainderEnabled = false,
  onAutoRemainderChange = () => {},
  isAddProperty = false,
  onAddPropertyClick,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const hasSearch = typeof onSearchChange === "function";

  const handleRangeChange = (dates) => {
    if (dates) {
      const [start, end] = dates;
      setStartDate(start ? start.toDate() : null);
      setEndDate(end ? end.toDate() : null);
      dateRangeFilter?.onChange?.(dates);
    } else {
      setStartDate(null);
      setEndDate(null);
      dateRangeFilter?.onChange?.([]);
    }
  };

  return (
    <div
      className={twMerge(
        "w-full bg-white rounded-lg p-4 md:p-6  lg:p-[30px] shadow-sm flex  gap-3 items-center justify-start",
        containerClass
      )}
    >
      {/* Search */}
      {hasSearch && (
        <div
          className={twMerge(
            "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] flex items-center w-full md:w-auto flex-grow md:flex-grow-0 gap-2",
            searchWrapperClass
          )}
        >
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearchSubmit?.()}
            outerContainerStyle="relative top-[6px]"
            inputStyle={twMerge(
              `h-[36px] sm:h-[39px] placeholder:font-inter md:h-[42px] lg:h-[45px] pl-10 pr-3 border border-[#81818180]  py-2 rounded-[5px]  placeholder:text-opacity-100 placeholder:text-[#9D9D9D] text-[12px] sm:text-[13px] md:text-[14px] lg:text-[15px] sm:placeholder:text-[11px] md:placeholder:text-[13px] lg:placeholder:text-[15px] focus:outline-none focus:border-indigo-500`,
              searchInputClass
            )}
            className="text-sm"
            searchIcon={true}
          />
        </div>
      )}

      {/* Filters */}
      {filters?.length > 0 &&
        filters.map((filter, index) => (
          <div
            className={twMerge("w-full md:w-auto", dropdownWrapperClass)}
            key={index}
          >
            <DropDownNew
              name={filter.name}
              //label={filter.name}
              options={filter.options}
              defaultOption={
                filter.value
                  ? { label: filter.value, value: filter.value }
                  : null
              }
              onSelect={(val) => filter.onChange(val?.value)}
              placeholder={filter.name}
              displayKey="label"
              idKey="value"
              isForm
              buttonClassName={twMerge(
                "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px]  text-[13px]  rounded-[5px] border border-[#81818180]",
                filterDropdownClass
              )}
              dropdownClassName={twMerge(
                "w-full mt-1 text-[#9D9D9D] rounded-[0px]"
              )}
              labelStyle="text-sm"
              placeholderClass="text-[10px] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#9D9D9D] text-opacity-100 font-normal font-inter"
/>
          </div>
        ))}

      {/* Date Range  */}
      {dateRangeFilter && (
        <div className={twMerge("w-full md:w-auto", datePickerClass)}>
          <DropDownNew
            name="dateRange"
            // label="Date Range"
            options={dateRangeFilter.options}
            defaultOption={
              dateRangeFilter.value
                ? { label: dateRangeFilter.value, value: dateRangeFilter.value }
                : null
            }
            onSelect={(val) => dateRangeFilter.onChange(val?.value)}
            placeholder="Filter by Date Range"
            displayKey="label"
            idKey="value"
            isForm
            buttonClassName={twMerge(
              "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px]  text-[13px]  rounded-[5px] border border-[#81818180]",
              filterDropdownClass
            )}
            dropdownClassName={twMerge(
              "w-full mt-1 text-[#9D9D9D] rounded-[0px]"
            )}
            labelStyle="text-sm"
            placeholderClass="text-opacity-100 font-normal font-inter text-[10px] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#9D9D9D]"
     />
        </div>
      )}
      {/* Date Picker */}
      {datePicker && (
        <div className={twMerge("w-full md:w-auto", datePickerClass)}>
          <RangePicker
            value={[
              datePicker.startDate ? dayjs(datePicker.startDate) : null,
              datePicker.endDate ? dayjs(datePicker.endDate) : null,
            ]}
            format="YYYY-MM-DD"
            onChange={(dates) => {
              if (dates) {
                datePicker.onChange("startDate", dates[0]?.toDate());
                datePicker.onChange("endDate", dates[1]?.toDate());
              } else {
                datePicker.onChange("startDate", null);
                datePicker.onChange("endDate", null);
              }
            }}
            suffixIcon={
              <span className="cursor-pointer">
                <AiOutlineCalendar size={12} style={{ color: "#7B7B7B" }} />
              </span>
            }
            className={twMerge(
              "h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] rounded-[5px] border border-[#C5C5C5] text-gray-800",
              "w-full sm:w-[278px] md:w-[227px] lg:w-[200px] xl:w-[247px]"
            )}
            style={{
              backgroundColor: "white",
            }}
            placeholder={["Start date", "End date"]}
            inputReadOnly={true}
          />
        </div>
      )}

      {/* Buttons */}
      {buttons?.length > 0 && (
        <div
          className={twMerge(
            "flex gap-2",
            "w-auto text-[8px] sm:text-[9px] md:text-[10px] lg:text-[11px]",
            // "sm:mr-auto", <-- This aligns to right on sm and up
            `${
              isAutoRemainder || isAddProperty
                ? "sm:mr-auto"
                : "justify-end sm:ml-auto"
            }`,
            buttonWrapperClass
          )}
        >
          {buttons.map((btn, index) => (
            <CustomButton
              key={index}
              buttonText={btn.label}
              onClick={btn.onClick}
              className={twMerge(
                `${
                  btn.className ||
                  (btn.variant === "main"
                    ? "bg-[#9333EA] text-white"
                    : btn.variant === "secondary"
                    ? "bg-[#C084FC] text-white border border-[#873FE2B0]"
                    : "bg-transparent text-[#873FE2] border border-[#873FE2]")
                } font-inter text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase`,
                !btn.className
                  ? "w-full xs:w-[83px] " + buttonClass
                  : btn.className
              )} 
              textWeight="font-medium"
              loading={btn.loading}
              disabled={btn.disabled}
            />
          ))}
        </div>
      )}

      {/* Auto Remainder */}
      {isAutoRemainder && (
        <div className="flex items-center justify-end max-w-[180px] md:max-w-[220px]  [@media(min-width:1024px)]:max-w-[200px]   [@media(min-width:1180px)]:max-w-[242px] mr-0 ml-auto">
          <div className="flex border border-[#CECECE] py-[10px] px-3 rounded-[5px] h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] w-full sm:w-[242px] items-center justify-between gap-2">
            <span className="text-[9px] sm:text-[11px] md:text-[13px] lg:text-[15px] text-[#9D9D9D] font-normal font-inter leading-[100%] tracking-[0%]">
              Auto-Reminders
            </span>
            <Switch
              className=""
              loading={isUserLoading}
              disabled={isAutoRemainderDisabled}
              checked={isAutoRemainderEnabled}
              onChange={onAutoRemainderChange}
            />
          </div>
        </div>
      )}

      {/* Add Property Button */}
      {isAddProperty && (
        <div className="flex items-center justify-end w-full mx-auto flex-frow xs:mr-0 xs:w-auto">
          <CustomButton
            buttonText="+ Add Property"
            onClick={() => onAddPropertyClick?.()}
            className="text-[9px] sm:text-[11px] md:text-[13px] lg:text-[15px] bg-[#4772F1] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] w-full xs:w-[125px] sm:w-[160px] md:w-[140px] lg:w-[150px] xl:w-[170px]"
          />
        </div>
      )}
    </div>
  );
};

export default TableHeader;
